import axios from 'axios';

import {isNotNil} from 'ramda';

import type {
  CreateDataQueryResponse,
  GetCountByQueryResponse,
  GetDataByQueryResponse,
  GetDataIdsByQuery,
  GetDataQueryRequest,
  GetDataQueryResponse,
  GetSummaryRowResponse,
  QueryIdRequestBody,
} from '../types/api/data';
import {isApiException} from '../utils/isApiException';
import {AXIOS_BASE_CONFIG} from './axiosConfig';
import {gridDataProtocol} from './gridDataProtocol';
import {handleCopyError} from './handleCopyError';
import {RequestCancelledError} from './requestCancelledError';
import type {ApiContext} from './types';

/**
 * Statuses that mean the user is not allowed to read this grid's data at all. Retrying cannot fix
 * them, so they are the one class of read failure that still belongs on the grid-level error
 * channel instead of the Retry overlay.
 */
const AUTHORIZATION_FAILURE_STATUS_CODES = [401, 403];

/**
 * Failure handler for the RUNTIME data reads (/count, /data, /summary). Resolves `undefined` so
 * `GridDataEngine` settles `fetchState: 'error'` (recoverable Retry overlay) instead of
 * `handleApiError` replacing the whole grid with a terminal screen. 401/403 and cancels are the
 * exceptions.
 */
const createRecoverableFetchErrorHandler = (ctx: ApiContext) => (error: unknown) => {
  if (axios.isCancel(error)) {
    throw new RequestCancelledError();
  }

  const status = axios.isAxiosError(error) ? error.response?.status : undefined;

  if (isNotNil(status) && AUTHORIZATION_FAILURE_STATUS_CODES.includes(status)) {
    ctx.handleApiError(error);
  }

  return undefined;
};

export function createDataApi(ctx: ApiContext) {
  const BASE = `/v6/data-grid/${ctx.gridCode}`;
  const handleRecoverableFetchError = createRecoverableFetchErrorHandler(ctx);

  return {
    createDataQuery: (
      params: QueryIdRequestBody,
      signal?: AbortSignal,
      options?: {forCopy?: boolean}
    ) =>
      axios
        .post<CreateDataQueryResponse>(`${BASE}/data-query`, params, {...AXIOS_BASE_CONFIG, signal})
        .then((response) => response.data)
        .catch((error: unknown) =>
          options?.forCopy ? handleCopyError(ctx, error) : ctx.handleApiError(error)
        ),
    getDataByQuery: (
      dataQueryId: string,
      offset: number,
      limit: number,
      hasSmartSearch: boolean,
      columns: string[],
      signal?: AbortSignal
    ) =>
      axios
        .post<GetDataByQueryResponse>(
          gridDataProtocol.pageUrl({
            gridCode: ctx.gridCode,
            queryId: dataQueryId,
            offset,
            limit,
            hasSmartSearch,
          }),
          {columns},
          {...AXIOS_BASE_CONFIG, signal}
        )
        .then((response) => response.data.rows)
        .catch(handleRecoverableFetchError),
    // Best-effort: no signal to cancel, and a missing summary is never worth the grid.
    getSummaryRow: (dataQueryId: string) =>
      axios
        .get<GetSummaryRowResponse>(`${BASE}/data/${dataQueryId}/summary`, AXIOS_BASE_CONFIG)
        .then((response) => response.data.summaryRow.cells)
        .catch(() => null),
    getCountByQuery: (dataQueryId: string, signal?: AbortSignal) =>
      axios
        .get<GetCountByQueryResponse>(`${BASE}/data/${dataQueryId}/count`, {
          ...AXIOS_BASE_CONFIG,
          signal,
        })
        .then((response) => response.data.totalRows)
        .catch(handleRecoverableFetchError),
    getDataIdsByQuery: (
      dataQueryId: string,
      offset: number,
      limit: number,
      hasSmartSearch: boolean
    ) =>
      axios
        .get<GetDataIdsByQuery[]>(
          gridDataProtocol.pageUrl(
            {gridCode: ctx.gridCode, queryId: dataQueryId, offset, limit, hasSmartSearch},
            '/id'
          ),
          AXIOS_BASE_CONFIG
        )
        .then((response) => response.data)
        .catch(ctx.handleApiError),
    getDataQuery: (dataQueryId: GetDataQueryRequest, options?: {forCopy?: boolean}) =>
      axios
        .get<GetDataQueryResponse>(`${BASE}/data-query/${dataQueryId}`, AXIOS_BASE_CONFIG)
        .then((response) => response.data)
        .catch((error: Error & {response?: {data: unknown}}) => {
          const errorData = error?.response?.data;
          const isErrorDG0010 =
            isApiException(errorData) &&
            errorData?.errors?.some((error) => error.code === 'PKG-DG-0010');

          if (isErrorDG0010) {
            throw error;
          }

          return options?.forCopy ? handleCopyError(ctx, error) : ctx.handleApiError(error);
        }),
  };
}
